import { FC, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Divider } from 'components/Divider/Divider';
import { Button } from 'components/Form/Button';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QuantityDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { TransactionsRoutes } from 'support/types';

type QuantityDiscrepancyViewProps = Pick<QuantityDiscrepancy, 'data'>;
export const QuantityDiscrepancyView: FC<QuantityDiscrepancyViewProps> = ({ data }) => {
  const { t } = useTranslation();
  const { open_quantity: openQuantity, ordered_quantity: orderedQuantity, invoiced_quantity: invoicedQuantity } = data;
  const [isOpenQuantityDetailsVisible, setIsOpenQuantityDetailsVisible] = useState(false);

  return (
    <div className="flex flex-col">
      <div className="grid w-full grid-cols-2 text-xs text-gray-500 [&>.contents:not(:first-child)>*]:pt-5">
        <div className="contents">
          <span>
            {t('invoiceVerification:detailsPage.discrepanciesModal.content.quantityDiscrepancy.rows.invoicedQuantity')}
          </span>
          <span className="text-end text-gray-900">{invoicedQuantity.value ?? EMPTY_FIELD}</span>
        </div>

        <div className="contents">
          <div className="flex items-center gap-1">
            {openQuantity.breakdown.length ? (
              <Button
                variant="text"
                iconOnly
                size="extra-small"
                LeftIcon={ChevronRightIcon}
                className={classNames(
                  isOpenQuantityDetailsVisible ? 'rotate-90' : 'rotate-0',
                  'transition-transform !text-gray-500',
                )}
                onClick={() => setIsOpenQuantityDetailsVisible(!isOpenQuantityDetailsVisible)}
                analyticsId={
                  'invoiceVerification:detailsPage.discrepanciesModal.quantityDiscrepancy.toggleQuantityDetails'
                }
              />
            ) : null}
            <span>
              {t('invoiceVerification:detailsPage.discrepanciesModal.content.quantityDiscrepancy.rows.openQuantity')}
            </span>
          </div>
          <span className="text-end text-gray-900">{openQuantity.value ?? EMPTY_FIELD}</span>
          <div
            className={classNames(
              'overflow-hidden transition-all duration-300 ease-in-out col-span-2',
              isOpenQuantityDetailsVisible ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 pointer-events-none -mt-5',
            )}
          >
            <Divider />
            <div className="mt-5 grid w-full grid-cols-3 gap-y-5 text-xs text-gray-500">
              <div className="contents">
                <span>
                  {t(
                    'invoiceVerification:detailsPage.discrepanciesModal.content.quantityDiscrepancy.openQuantityDetails.headers.document',
                  )}
                </span>
                <span className="text-end text-gray-900">
                  {t(
                    'invoiceVerification:detailsPage.discrepanciesModal.content.quantityDiscrepancy.openQuantityDetails.headers.quantity',
                  )}
                </span>
                <span className="text-end text-gray-900">
                  {t(
                    'invoiceVerification:detailsPage.discrepanciesModal.content.quantityDiscrepancy.openQuantityDetails.headers.openQuantity',
                  )}
                </span>
              </div>
              {openQuantity.breakdown.map((breakdown, index) => (
                <div key={index} className="contents">
                  <div className="flex flex-col">
                    <span>{t(`common:messageTypes.singular.${breakdown.message.type}`)}:</span>
                    <Link
                      target="_blank"
                      to={routeToPage(TransactionsRoutes.viewMessage, { id: breakdown.message.id })}
                      className="text-gray-700 underline hover:text-procuros-green-600"
                    >
                      {breakdown.message.external_message_reference ?? EMPTY_FIELD}
                    </Link>
                  </div>
                  <span className="text-end text-gray-900">{breakdown.action ?? EMPTY_FIELD}</span>
                  <span className="text-end text-gray-900">{breakdown.after ?? EMPTY_FIELD}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="contents">
          <span>
            {t('invoiceVerification:detailsPage.discrepanciesModal.content.quantityDiscrepancy.rows.orderedQuantity')}
          </span>
          <span className="text-end text-gray-900">{orderedQuantity.value ?? EMPTY_FIELD}</span>
        </div>
      </div>
    </div>
  );
};
